// Access verification that survives a bad network.
//
// Every launch checks the signed-in email against `allowedEmails` before the
// app renders. The check used to fail closed by signing out, which conflated
// two very different answers: "this email is not allowed" and "I could not
// reach the database". The second one happens constantly — a cold start
// before the network is up, airplane mode, a captive wifi portal, a dropped
// Realtime Database socket — and `signOut()` deletes the persisted Firebase
// credential, so the next launch asks for a password again. That is the
// re-login loop, not an expiring session.
//
// So: only a completed read can deny. Anything else leaves the session alone
// and retries. Security is unchanged — the database rules, not this check,
// are what actually keep a stranger's reads out.

import { ref, get, onValue } from 'firebase/database';
import { database } from '../firebase';

const GRANT_KEY = 'prisma:accessGrant';

// How long a verified allow stands in for a live check while offline. Long
// enough to cover a trip without service, short enough that a removed email
// loses its head start on this device reasonably soon.
export const GRANT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Safari in private mode throws on localStorage rather than returning null,
// and a wiped store is a normal state, not an error — degrade to "no grant".
export function readGrant() {
  try {
    const raw = localStorage.getItem(GRANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.email !== 'string' || typeof parsed.at !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveGrant(email, now = Date.now()) {
  try {
    localStorage.setItem(GRANT_KEY, JSON.stringify({ email, at: now }));
  } catch {
    // No cache available; every launch just has to reach the network.
  }
}

export function clearGrant() {
  try {
    localStorage.removeItem(GRANT_KEY);
  } catch {
    // Nothing to clear.
  }
}

export function hasValidGrant(email, now = Date.now()) {
  const grant = readGrant();
  if (!grant || !email) return false;
  if (grant.email !== email) return false;
  return now - grant.at < GRANT_TTL_MS;
}

// `allowedEmails` is written as an object of arbitrary keys to emails, but an
// array-ish shape (numeric keys) comes back as an array — handle both.
export function isEmailAllowed(value, email) {
  if (!value || !email) return false;
  const emails = Array.isArray(value) ? value : Object.values(value);
  return emails.includes(email);
}

// A rules rejection is a real answer: the read completed and the server said
// no. Everything else — offline, timeout, socket closed — is "ask again".
export function isDefinitiveDenial(err) {
  const code = String(err?.code || '').toLowerCase();
  const message = String(err?.message || '').toLowerCase();
  return code.includes('permission_denied') || message.includes('permission denied');
}

async function fetchAllowed(email) {
  const snap = await get(ref(database, 'allowedEmails'));
  return snap.exists() ? isEmailAllowed(snap.val(), email) : false;
}

/// Resolve access without any side effects on the session.
///
/// - `allowed`: the read completed and the email is on the list.
/// - `denied`:  the read completed (or was rejected by rules) and it is not.
/// - `unverified`: the read never completed. `cachedGrant` says whether this
///   device verified this same email recently enough to run on that instead.
export async function resolveAccess(email, { check = fetchAllowed, now = Date.now() } = {}) {
  try {
    const allowed = await check(email);
    if (allowed) {
      saveGrant(email, now);
      return { status: 'allowed' };
    }
    clearGrant();
    return { status: 'denied' };
  } catch (err) {
    if (isDefinitiveDenial(err)) {
      clearGrant();
      return { status: 'denied' };
    }
    return { status: 'unverified', cachedGrant: hasValidGrant(email, now) };
  }
}

/// Call `cb` when connectivity comes back, so a retry does not have to wait
/// out its backoff. Fires on a false -> true transition only; subscribing
/// while already connected must not re-trigger the check that just failed.
export function onReconnect(cb) {
  let wasConnected = null;
  let stopDb = () => {};
  try {
    stopDb = onValue(
      ref(database, '.info/connected'),
      (snap) => {
        const connected = snap?.val() === true;
        if (connected && wasConnected === false) cb();
        wasConnected = connected;
      },
      () => {},
    );
  } catch {
    stopDb = () => {};
  }

  const onOnline = () => cb();
  window.addEventListener('online', onOnline);

  return () => {
    if (typeof stopDb === 'function') stopDb();
    window.removeEventListener('online', onOnline);
  };
}
